'use client';

import type { ReactNode } from 'react';
import { PortalLink } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { resizeImageUrl } from '../_shared/resizeImageUrl';

// ---------------------------------------------------------------------------
// LastCourseCard — the "continue where you left off" tile.
//
// Extracted from {@link LastCourse} so other blocks can render the exact same
// item instead of re-implementing it (the journey-detail page's "continue"
// slot is the first such consumer). The class names and DOM are unchanged from
// the original block, so the stylesheet below still drives LastCourse's own
// grid and loading skeleton.
//
// Purely presentational: every value it shows arrives resolved. Data shaping
// (remaining minutes, resume-point subtitle, href templating) stays with the
// caller, since each host reads it off a different payload.
// ---------------------------------------------------------------------------

export interface LastCourseCardProps {
  title: string;
  /** Resume point ("6. Bölüm: …") or the course blurb. */
  subtitle?: string;
  thumbnail?: string;
  /** Completion percentage, 0–100. */
  progress: number;
  /** Footer text left of the CTA, e.g. "Kalan Süre: 45dk". */
  meta?: string;
  ctaLabel?: string;
  showCta?: boolean;
  href: string;
  /** 'full' (default): the whole card is the link, and the CTA is a decorative
   *  chip. 'button': only the CTA navigates — the card itself is inert. */
  linkMode?: 'full' | 'button';
  /** Palette override; omit to follow the host's design tokens. */
  cardTheme?: 'dark' | 'light';
  /** Intercept navigation instead of letting the link follow `href` — used by
   *  hosts that must do work first (the journey page registers the learner on
   *  the course before sending them to it). */
  onNavigate?: (href: string) => void;
}

/** The card's stylesheet. Exported as a string so a host can inline it once
 *  next to several cards rather than per card. */
export const lastCourseCardCss = `
      .last-course-grid {
        display: grid;
        grid-template-columns: repeat(var(--lc-cols, 1), minmax(0, 1fr));
        gap: clamp(12px, 2vw, 20px);
      }

      /* ---- Card ------------------------------------------------------- */
      /* The palette lives in --lc-* variables so the whole card flips with a
         single data-lc-theme attribute. The base block is the token-driven
         ("auto"/"light") palette; the dark block below overrides it. */
      .last-course-card {
        --lc-pad: clamp(12px, 1.6vw, 18px);
        --lc-gap: clamp(14px, 2vw, 22px);
        --lc-surface: var(--color-surface-card, #fff);
        --lc-border: var(--color-border-subtle, #e4e4e7);
        --lc-title: var(--color-text-primary, #18181b);
        --lc-muted: var(--color-text-secondary, #71717a);
        --lc-track: var(--color-neutral-100, #f4f4f5);
        --lc-accent-from: var(--color-brand-500, #3b5bdb);
        --lc-accent-to: var(--color-brand-400, #6b8bff);
        /* Inverted CTA — a filled chip that always reads against the card
           surface. Set explicitly per theme instead of relying on
           primary/primary-foreground, which collapses to white-on-white
           once the host flips to dark. */
        --lc-cta-bg: var(--color-text-primary, #18181b);
        --lc-cta-fg: var(--color-surface-card, #fff);
        --lc-cta-bg-hover: color-mix(in srgb, var(--color-text-primary, #18181b) 82%, #fff);

        display: grid;
        grid-template-columns: clamp(140px, 30%, 240px) 1fr;
        align-items: center;
        gap: var(--lc-gap);
        padding: var(--lc-pad);
        border-radius: var(--radius-xl, 16px);
        border: 1px solid var(--lc-border);
        background: var(--lc-surface);
        color: var(--lc-title);
        text-decoration: none;
        transition: border-color 0.15s ease;
        /* Rendered as the Card component: this unlayered rule keeps grid /
           padding / the theme-aware border, and overflow:visible lets the
           Card's shadow-(--shadow-brand) show (the component sets
           overflow:hidden). */
        overflow: visible;
      }
      .last-course-card[data-lc-theme='dark'] {
        --lc-surface: #0b0d12;
        --lc-border: rgba(255, 255, 255, 0.08);
        --lc-title: #f4f6f9;
        --lc-muted: #9aa2b1;
        --lc-track: rgba(255, 255, 255, 0.12);
        --lc-accent-from: #3757ea;
        --lc-accent-to: #6b8bff;
        --lc-cta-bg: #ffffff;
        --lc-cta-fg: #12141a;
        --lc-cta-bg-hover: #e7e9ee;
      }
      a.last-course-card:hover {
        border-color: color-mix(in srgb, var(--lc-title) 28%, transparent);
      }

      .last-course-thumbnail {
        position: relative;
        width: 100%;
        aspect-ratio: var(--app-course-image-aspect-ratio, 16 / 10);
        border-radius: var(--radius-lg, 12px);
        overflow: hidden;
      }
      .last-course-thumbnail > img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
      }

      /* ---- Content ---------------------------------------------------- */
      .last-course-body {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 8px;
        min-width: 0;
      }
      .last-course-title {
        font-size: clamp(15px, 1.35vw, 19px);
        font-weight: 700;
        line-height: 1.3;
        color: var(--lc-title);
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      /* Always occupies one line's worth of space, even with no subtitle —
         otherwise cards without one sit shorter than their row siblings and
         the progress bars stop lining up across the row. */
      .last-course-sub {
        margin: 0;
        min-height: 1.45em;
        font-size: 12.5px;
        line-height: 1.45;
        color: var(--lc-muted);
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .last-course-progress {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 2px;
      }
      .last-course-track {
        flex: 1;
        min-width: 0;
        height: 8px;
        border-radius: 999px;
        background: var(--lc-track);
        overflow: hidden;
      }
      .last-course-fill {
        height: 100%;
        border-radius: 999px;
        background: linear-gradient(90deg, var(--lc-accent-from), var(--lc-accent-to));
        transition: width 0.3s ease;
      }
      .last-course-pct {
        flex-shrink: 0;
        font-size: 12px;
        color: var(--lc-muted);
        font-variant-numeric: tabular-nums;
      }
      .last-course-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: 2px;
      }
      .last-course-meta {
        font-size: 12px;
        color: var(--lc-muted);
      }
      .last-course-cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        height: 36px;
        padding: 0 18px;
        border-radius: var(--radius-button, var(--radius-md, 8px));
        background: var(--color-brand-500, #3b5bdb);
        color: var(--lc-cta-fg);
        font-size: 13px;
        font-weight: 600;
        font-family: inherit;
        white-space: nowrap;
        text-decoration: none;
        border: 0;
        cursor: pointer;
        transition: background 0.15s ease;
      }
      a.last-course-card:hover .last-course-cta,
      .last-course-cta:hover {
        background: var(--lc-cta-bg-hover);
      }

      /* ---- Skeleton --------------------------------------------------- */
      .last-course-skeleton {
        background: var(--lc-track);
        border-radius: 6px;
        animation: lc-pulse 1.5s ease-in-out infinite;
      }
      @keyframes lc-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }

      /* Tablet: a horizontal card gets too narrow when 2 share a row, so the
         grid drops to a single column and the two cards stack. The card keeps
         its HORIZONTAL layout (thumbnail beside the content) at every
         breakpoint — only the number of cards per row changes. */
      @media (max-width: 1370px) {
        .last-course-grid,
        .last-course-grid--row {
          grid-template-columns: 1fr;
        }
      }

      /* Phone: still horizontal, just a narrower thumbnail column so the
         title/progress keep usable width, and a wrapping footer. */
      @media (max-width: 600px) {
        .last-course-card {
          --lc-gap: 12px;
          grid-template-columns: clamp(96px, 34%, 140px) 1fr;
        }
        .last-course-foot {
          flex-wrap: wrap;
        }
        .last-course-cta {
          flex: 1;
        }
      }
    `;

/** Inline `<style>` carrying {@link lastCourseCardCss}. */
export function LastCourseCardStyles(): ReactNode {
  return <style>{lastCourseCardCss}</style>;
}

export function LastCourseCard({
  title,
  subtitle,
  thumbnail,
  progress,
  meta,
  ctaLabel,
  showCta = true,
  href,
  linkMode = 'full',
  cardTheme,
  onNavigate,
}: LastCourseCardProps): ReactNode {
  const pct = Math.min(100, Math.max(0, progress));
  const isButtonLinkMode = linkMode === 'button';
  // In button-link mode the card itself isn't clickable, so the button is the
  // only way to reach the course — always show it regardless of `showCta`.
  const resolvedShowCta = isButtonLinkMode ? true : showCta;
  const intercept = onNavigate
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        onNavigate(href);
      }
    : undefined;

  const cta = onNavigate ? (
    <a href={href} className="last-course-cta" onClick={intercept}>
      {ctaLabel}
    </a>
  ) : (
    <PortalLink href={href} className="last-course-cta">
      {ctaLabel}
    </PortalLink>
  );

  const body = (
    <>
      <div className="last-course-thumbnail">
        {thumbnail ? (
          // The thumbnail column caps at 240 CSS px (see .last-course-card's
          // grid-template-columns), so 480px covers 2x displays without
          // pulling the full-resolution original. Width-only: the card crops
          // to its own aspect ratio via object-fit.
          <img src={resizeImageUrl(thumbnail, '480x0')} alt={title} />
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(135deg, var(--color-brand-100, #dbeafe), var(--color-brand-200, #bfdbfe))',
            }}
          />
        )}
      </div>

      <div className="last-course-body">
        <div className="last-course-title">{title}</div>
        <p className="last-course-sub">{subtitle}</p>

        {/* Completion bar with the percentage trailing it, as in the
            reference design. */}
        <div className="last-course-progress">
          <div className="last-course-track">
            <div className="last-course-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="last-course-pct">%{Math.round(pct)}</span>
        </div>

        {(meta || resolvedShowCta) && (
          <div className="last-course-foot">
            <span className="last-course-meta">{meta ?? ''}</span>
            {resolvedShowCta &&
              (isButtonLinkMode ? (
                cta
              ) : (
                // Card-wide link mode: the whole card is the anchor, so this
                // is a decorative chip, not a second target.
                <span className="last-course-cta" aria-hidden>
                  {ctaLabel}
                </span>
              ))}
          </div>
        )}
      </div>
    </>
  );

  // Rendered as `<Card>` for the shared elevation (`shadow-(--shadow-brand)`).
  // `ring-0` keeps `<Card>`'s brand ring off — `.last-course-card` has its own
  // theme-aware (`data-lc-theme`) border + hover-colour that must stay.
  if (isButtonLinkMode) {
    return (
      <Card className="ring-0 last-course-card" data-lc-theme={cardTheme}>
        {body}
      </Card>
    );
  }

  if (onNavigate) {
    return (
      <Card asChild className="ring-0">
        <a href={href} className="last-course-card" data-lc-theme={cardTheme} onClick={intercept}>
          {body}
        </a>
      </Card>
    );
  }

  return (
    <Card asChild className="ring-0">
      <PortalLink href={href} className="last-course-card" data-lc-theme={cardTheme}>
        {body}
      </PortalLink>
    </Card>
  );
}

/** Loading placeholder with the card's own silhouette. */
export function LastCourseCardSkeleton({ cardTheme }: { cardTheme?: 'dark' | 'light' }): ReactNode {
  return (
    <Card className="ring-0 last-course-card" data-lc-theme={cardTheme}>
      <div className="last-course-thumbnail last-course-skeleton" />
      <div className="last-course-body">
        <div className="last-course-skeleton" style={{ height: 20, width: '70%' }} />
        <div className="last-course-skeleton" style={{ height: 12, width: '90%' }} />
        <div className="last-course-progress">
          <div className="last-course-track last-course-skeleton" />
          <div className="last-course-skeleton" style={{ height: 12, width: 32 }} />
        </div>
        <div className="last-course-foot">
          <div className="last-course-skeleton" style={{ height: 12, width: 110 }} />
          <div className="last-course-skeleton" style={{ height: 36, width: 100 }} />
        </div>
      </div>
    </Card>
  );
}
